import { useLocation } from "@tanstack/react-router";
import { cn } from "@/shared/lib/utils";
import {
  getDevStatus,
  DEV_STATUS_META,
  type DevStatus,
} from "@/shared/lib/dev-status";

type PageHeaderVariant = "default" | "compact";

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  className?: string;
  variant?: PageHeaderVariant;
}

function DevStatusBadge({ status }: { status: DevStatus }) {
  const meta = DEV_STATUS_META[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset",
        meta.badgeClass,
      )}
      title={meta.description}
    >
      <span className={cn("size-1.5 rounded-full", meta.dotClass)} />
      {meta.shortLabel}
    </span>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  className,
  variant = "default",
}: PageHeaderProps) {
  const { pathname } = useLocation();
  const devStatus = getDevStatus(pathname);
  const compact = variant === "compact";

  return (
    <div
      className={cn(
        "flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between",
        compact ? undefined : "mb-6",
        className,
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h1
            className={cn(
              "truncate tracking-tight",
              compact
                ? "text-[15px] font-semibold"
                : "text-xl font-bold",
            )}
          >
            {title}
          </h1>
          {devStatus && devStatus !== "static" && (
            <DevStatusBadge status={devStatus} />
          )}
        </div>
        {description && (
          <p
            className={cn(
              "mt-0.5 text-text-tertiary",
              compact ? "text-[11px]" : "text-[13px]",
            )}
          >
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div
          className={cn(
            "flex items-center gap-2",
            compact ? "sm:mt-0" : "mt-3 sm:mt-0",
          )}
        >
          {actions}
        </div>
      )}
    </div>
  );
}
